/**
 * UsersGroupsTab Component (H.19)
 * 
 * Tab content for viewing users and groups with their effective permissions.
 * Shows tenant members list and group management (create, manage members, delete).
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import { userDisplayLabel } from '@/lib/user-label';
import { 
  Button, 
  Badge, 
  Skeleton, 
  Icon,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LabeledInput } from '@/components/ui/form-wrappers';
import { toast } from 'sonner';
import { User, Users, X } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { useTenantMembers, useAddTenantMember, useLookupIdentityByEmail, useRemoveTenantMember, type IdentityLookup } from '@/features/permissions/hooks';
import { 
  useGroups, 
  useGroupMembers,
  useCreateGroup, 
  useDeleteGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  type Group, 
} from '@/hooks/use-groups';

// =============================================================================
// Add User Dialog
// =============================================================================

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AddUserDialog({ open, onOpenChange }: AddUserDialogProps) {
  const { t } = useI18n();
  const lookupMutation = useLookupIdentityByEmail();
  const addMutation = useAddTenantMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [found, setFound] = useState<IdentityLookup | null>(null);
  const [lookupError, setLookupError] = useState('');

  const { currentTenant } = useTenant();

  const handleLookup = async () => {
    if (!email.trim()) return;
    setFound(null);
    setLookupError('');
    try {
      // ISS-463: el tenant activo se captura en el submit, no en el closure.
      const tenantId = currentTenant?.id ?? '';
      if (!tenantId) {
        setLookupError('No active tenant');
        return;
      }
      const result = await lookupMutation.mutateAsync({ email: email.trim(), tenantId });
      setFound(result);
    } catch {
      setLookupError(
        t('admin.permissions.usersGroups.userNotFound') || `No user found with email: ${email}`
      );
    }
  };

  const handleAdd = async () => {
    if (!found) return;
    try {
      await addMutation.mutateAsync({ user_id: found.id, role });
      toast.success(t('admin.permissions.usersGroups.userAdded') || 'User added to tenant');
      handleClose();
    } catch {
      toast.error(t('admin.permissions.usersGroups.addUserError') || 'Failed to add user');
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('member');
    setFound(null);
    setLookupError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('admin.permissions.usersGroups.addUserTitle') || 'Add User to Tenant'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Email lookup */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              {t('admin.permissions.usersGroups.lookupByEmail') || 'User Email'}
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                className="flex-1 h-9 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                placeholder="user@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setFound(null); setLookupError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleLookup}
                disabled={!email.trim() || lookupMutation.isPending}
              >
                {lookupMutation.isPending ? (
                  <Icon name="loader" size={14} className="animate-spin" />
                ) : (
                  t('common.search') || 'Search'
                )}
              </Button>
            </div>
            {lookupError && (
              <p className="text-xs text-red-500 mt-1">{lookupError}</p>
            )}
          </div>

          {/* Found user preview */}
          {found && (
            <div className="rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 p-3 flex items-center gap-2">
              <User className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
              <div className="min-w-0">
                {found.name && <p className="font-medium text-sm text-green-800 dark:text-green-300">{found.name}</p>}
                <p className="text-xs text-green-700 dark:text-green-400">{found.email}</p>
              </div>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              {t('admin.permissions.usersGroups.role') || 'Role'}
            </label>
            <select
              className="w-full h-9 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!found || addMutation.isPending}
          >
            {addMutation.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Adding...</>
            ) : (
              t('admin.permissions.usersGroups.addUser') || 'Add User'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Users Sub-Tab — Tenant Members List
// =============================================================================

function UsersList() {
  const { t } = useI18n();
  const { data: tenantMembers, isLoading, error } = useTenantMembers();
  const [showAddUser, setShowAddUser] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ user_id: string; label: string } | null>(null);
  const removeMember = useRemoveTenantMember();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('admin.permissions.usersGroups.usersError') || 'Failed to load users'}
      </div>
    );
  }

  const members = tenantMembers?.items || [];

  return (
    <>
      {members.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p>{t('admin.permissions.usersGroups.noUsers') || 'No tenant members found'}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowAddUser(true)}>
            <User className="h-4 w-4 mr-1.5" />
            {t('admin.permissions.usersGroups.addUser') || 'Add User'}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowAddUser(true)}>
              <User className="h-4 w-4 mr-1.5" />
              {t('admin.permissions.usersGroups.addUser') || 'Add User'}
            </Button>
          </div>
          {members.map((member) => (
            <div
              key={member.user_id}
              className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {userDisplayLabel(member.email, member.name, member.user_id, t('admin.permissions.emailUnavailable'))}
                  </span>
                  {member.email && member.name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {member.role && (
                  <Badge variant="secondary" className="text-xs">{t(`admin.permissions.tenantRoles.${member.role}`)}</Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700"
                  title={t('admin.permissions.usersGroups.removeUser') || 'Remove user from tenant'}
                  onClick={() =>
                    setMemberToRemove({
                      user_id: member.user_id,
                      label: userDisplayLabel(member.email, member.name, member.user_id, t('admin.permissions.emailUnavailable')),
                    })
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddUserDialog open={showAddUser} onOpenChange={setShowAddUser} />

      <Dialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.permissions.usersGroups.removeUser') || 'Remove user'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {memberToRemove?.label}{' '}
            {t('admin.permissions.usersGroups.removeUserConfirm') || 'will lose access to this workspace.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToRemove(null)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              disabled={removeMember.isPending}
              onClick={async () => {
                if (!memberToRemove) return;
                try {
                  await removeMember.mutateAsync(memberToRemove.user_id);
                  setMemberToRemove(null);
                  toast.success(t('admin.permissions.usersGroups.userRemoved') || 'User removed');
                } catch {
                  toast.error(t('admin.permissions.usersGroups.removeError') || 'Failed to remove user');
                }
              }}
            >
              {t('common.remove') || 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =============================================================================
// Create Group Dialog
// =============================================================================

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateGroupDialog({ open, onOpenChange }: CreateGroupDialogProps) {
  const { t } = useI18n();
  const { currentTenant } = useTenant();
  const createGroup = useCreateGroup();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!name.trim() || !currentTenant?.id) return;
    try {
      await createGroup.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        type: 'custom',
        tenant_id: currentTenant.id,
      });
      toast.success(t('admin.permissions.usersGroups.createSuccess') || 'Group created');
      setName('');
      setDescription('');
      onOpenChange(false);
    } catch {
      toast.error(t('admin.permissions.usersGroups.createError') || 'Failed to create group');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('admin.permissions.usersGroups.createGroupTitle') || 'Create Group'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <LabeledInput
            label={t('admin.permissions.usersGroups.groupName') || 'Name'}
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="e.g. Network Engineers"
            required
          />
          <LabeledInput
            label={t('admin.permissions.usersGroups.groupDescription') || 'Description'}
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!name.trim() || createGroup.isPending}
          >
            {createGroup.isPending ? (
              <><Icon name="loader" size={16} className="animate-spin mr-1" /> Creating...</>
            ) : (
              t('admin.permissions.usersGroups.create') || 'Create'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Manage Group Dialog (view/add/remove members, delete group)
// =============================================================================

interface ManageGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group;
}

function ManageGroupDialog({ open, onOpenChange, group }: ManageGroupDialogProps) {
  const { t } = useI18n();
  const { data: membersData, isLoading } = useGroupMembers(group.id);
  const { data: tenantMembers } = useTenantMembers();
  const addMember = useAddGroupMember(group.id);
  const removeMember = useRemoveGroupMember(group.id);
  const deleteGroup = useDeleteGroup();

  const [selectedUserId, setSelectedUserId] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const members = membersData?.items || [];
  const memberIds = new Set(members.map(m => m.user_id));

  // Available users = tenant members not already in group
  const availableUsers = (tenantMembers?.items || []).filter(u => !memberIds.has(u.user_id));

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    try {
      await addMember.mutateAsync({ user_id: selectedUserId });
      toast.success(t('admin.permissions.usersGroups.memberAdded') || 'Member added');
      setSelectedUserId('');
    } catch {
      toast.error(t('admin.permissions.usersGroups.memberAddError') || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember.mutateAsync(userId);
      toast.success(t('admin.permissions.usersGroups.memberRemoved') || 'Member removed');
    } catch {
      toast.error(t('admin.permissions.usersGroups.memberRemoveError') || 'Failed to remove member');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await deleteGroup.mutateAsync(group.id);
      toast.success(t('admin.permissions.usersGroups.deleteSuccess') || 'Group deleted');
      onOpenChange(false);
    } catch {
      toast.error(t('admin.permissions.usersGroups.deleteError') || 'Failed to delete group');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {group.name}
            {group.type === 'system' && (
              <Badge variant="secondary" className="text-xs">{t('admin.permissions.usersGroups.systemGroup')}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {group.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{group.description}</p>
          )}

          {/* Add Member */}
          {group.type !== 'system' && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  {t('admin.permissions.usersGroups.addMember') || 'Add Member'}
                </label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                >
                  <option value="">{t('admin.permissions.usersGroups.selectUser') || 'Select user...'}</option>
                  {availableUsers.map(u => (
                    <option key={u.user_id} value={u.user_id}>
                      {userDisplayLabel(u.email, u.name, u.user_id, t('admin.permissions.emailUnavailable'))}
                    </option>
                  ))}
                </select>
              </div>
              <Button 
                size="sm" 
                onClick={handleAddMember} 
                disabled={!selectedUserId || addMember.isPending}
              >
                {addMember.isPending ? (
                  <Icon name="loader" size={14} className="animate-spin" />
                ) : (
                  t('common.add') || 'Add'
                )}
              </Button>
            </div>
          )}

          {/* Members List */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.permissions.usersGroups.members') || 'Members'} ({members.length})
            </h4>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : members.length > 0 ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {members.map(member => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span>{userDisplayLabel(member.email, member.name, member.user_id, t('admin.permissions.emailUnavailable'))}</span>
                    </div>
                    {group.type !== 'system' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-6 w-6 p-0"
                        onClick={() => handleRemoveMember(member.user_id)}
                        disabled={removeMember.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                {t('admin.permissions.usersGroups.noMembers') || 'No members in this group'}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {group.type !== 'system' && !showDeleteConfirm && (
              <Button 
                variant="outline" 
                className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t('admin.permissions.usersGroups.deleteGroup') || 'Delete Group'}
              </Button>
            )}
            {showDeleteConfirm && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-600">{t('common.confirmDelete') || 'Are you sure?'}</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteGroup}
                  disabled={deleteGroup.isPending}
                >
                  {deleteGroup.isPending ? (
                    <Icon name="loader" size={14} className="animate-spin" />
                  ) : (
                    t('common.confirm') || 'Confirm'
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                  {t('common.cancel') || 'Cancel'}
                </Button>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close') || 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Groups Sub-Tab
// =============================================================================

function GroupsList() {
  const { t } = useI18n();
  const { data: groupsData, isLoading, error } = useGroups();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [managingGroup, setManagingGroup] = useState<Group | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('admin.permissions.usersGroups.groupsError') || 'Failed to load groups'}
      </div>
    );
  }

  const groups = groupsData?.items || [];

  return (
    <>
      {groups.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p>{t('admin.permissions.usersGroups.noGroups') || 'No groups created yet'}</p>
          <Button 
            variant="outline" 
            size="sm" 
            leftIcon="plus" 
            className="mt-4"
            onClick={() => setShowCreateDialog(true)}
          >
            {t('admin.permissions.usersGroups.createGroup') || 'Create Group'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              leftIcon="plus"
              onClick={() => setShowCreateDialog(true)}
            >
              {t('admin.permissions.usersGroups.createGroup') || 'Create Group'}
            </Button>
          </div>
          {groups.map((group: Group) => (
            <div
              key={group.id}
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {group.name}
                    </span>
                    {group.type === 'system' && (
                      <Badge variant="secondary" className="text-xs">{t('admin.permissions.usersGroups.systemGroup')}</Badge>
                    )}
                  </div>
                  {group.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {group.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {group.member_count} {t('admin.permissions.usersGroups.members') || 'members'}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setManagingGroup(group)}
                >
                  {t('admin.permissions.usersGroups.manage') || 'Manage'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateGroupDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog} 
      />

      {managingGroup && (
        <ManageGroupDialog
          open={!!managingGroup}
          onOpenChange={open => { if (!open) setManagingGroup(null); }}
          group={managingGroup}
        />
      )}
    </>
  );
}

// =============================================================================
// UsersGroupsTab
// =============================================================================

export function UsersGroupsTab() {
  const { t } = useI18n();
  const [subTab, setSubTab] = useState('groups');

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('admin.permissions.usersGroups.usersTab') || 'Users'}
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {t('admin.permissions.usersGroups.groupsTab') || 'Groups'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersList />
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <GroupsList />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default UsersGroupsTab;
